import React, { useState, useEffect, useCallback } from "react";
import { fetchValidationList } from "../../../lib/researchApi";
import GovernanceBadge from "../shared/GovernanceBadge";
import OutcomeConfidencePanel from "../shared/OutcomeConfidencePanel";

const GOVERNANCE_STATES = ["approved", "pilot", "experimental", "preview_only", "degraded", "disabled"];
const STRATEGY_OPTIONS = ["wheel", "earnings", "tactical_spread", "regime_spread"];
const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

function pct(n) { return n != null ? `${(n * 100).toFixed(1)}%` : "—"; }
function fmtDate(s) { return s ? new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"; }

function FilterBar({ filters, setFilters }) {
    const set = (k, v) => setFilters(prev => ({ ...prev, [k]: v, page: 1 }));
    return (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
            <div>
                <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Strategy</div>
                <select
                    value={filters.strategy || ""}
                    onChange={e => set("strategy", e.target.value || undefined)}
                    style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 8px" }}
                >
                    <option value="">All</option>
                    {STRATEGY_OPTIONS.map(s => <option key={s} value={s}>{STRATEGY_LABELS[s]}</option>)}
                </select>
            </div>
            <div>
                <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Governance State</div>
                <select
                    value={filters.governance_state || ""}
                    onChange={e => set("governance_state", e.target.value || undefined)}
                    style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 8px" }}
                >
                    <option value="">All</option>
                    {GOVERNANCE_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <div>
                <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Max Overfit</div>
                <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    placeholder="e.g. 0.25"
                    value={filters.max_overfit_score ?? ""}
                    onChange={e => set("max_overfit_score", e.target.value ? parseFloat(e.target.value) : undefined)}
                    style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 8px", width: 100 }}
                />
            </div>
            <div>
                <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Max Ruin Prob</div>
                <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    placeholder="e.g. 0.15"
                    value={filters.max_ruin_probability ?? ""}
                    onChange={e => set("max_ruin_probability", e.target.value ? parseFloat(e.target.value) : undefined)}
                    style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 8px", width: 100 }}
                />
            </div>
            <div>
                <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Sort By</div>
                <select
                    value={filters.sort_by || "validation_timestamp"}
                    onChange={e => set("sort_by", e.target.value)}
                    style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 8px" }}
                >
                    <option value="validation_timestamp">Date</option>
                    <option value="overfit_score">Overfit Score</option>
                    <option value="strategy">Strategy</option>
                    <option value="governance_state">Governance State</option>
                </select>
            </div>
            <div>
                <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Order</div>
                <select
                    value={filters.sort_dir || "DESC"}
                    onChange={e => set("sort_dir", e.target.value)}
                    style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 6, color: "var(--savvy-text)", fontSize: "12px", padding: "5px 8px" }}
                >
                    <option value="DESC">Newest first</option>
                    <option value="ASC">Oldest first</option>
                </select>
            </div>
        </div>
    );
}

export default function ValidationHistorySection({ onSelectRun }) {
    const [filters, setFilters] = useState({ page: 1, page_size: 20, sort_by: "validation_timestamp", sort_dir: "DESC" });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        fetchValidationList(filters)
            .then(setResult)
            .catch((e) => setError(e?.response?.data?.error || "Failed to load validations"))
            .finally(() => setLoading(false));
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const items = result?.items || [];
    const pagination = result?.pagination || {};
    const totalPages = pagination.pages || 1;

    return (
        <div>
            <FilterBar filters={filters} setFilters={setFilters} />

            {loading && <div style={{ color: "var(--savvy-muted)", fontSize: "13px", padding: "24px 0", textAlign: "center" }}>Loading…</div>}
            {error && <div style={{ color: "#f87171", fontSize: "13px" }}>Error: {error}</div>}

            {!loading && !error && (
                <>
                    <div style={{ fontSize: "11px", color: "var(--savvy-muted)", marginBottom: 8 }}>
                        {pagination.total ?? 0} results · Page {pagination.page ?? 1} of {totalPages}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                                    {["ID", "Strategy", "State", "Outcome", "Overfit", "Ruin Prob", "Date", "Notes", ""].map(h => (
                                        <th key={h} style={{ textAlign: "left", fontSize: "10px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.05em", padding: "8px 10px", whiteSpace: "nowrap" }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(row => (
                                    <tr
                                        key={row.id}
                                        style={{
                                            borderBottom: "1px solid rgba(255,255,255,0.04)",
                                            cursor: onSelectRun ? "pointer" : "default",
                                        }}
                                    >
                                        <td style={{ padding: "9px 10px", fontSize: "11px", color: "var(--savvy-dim)", fontVariantNumeric: "tabular-nums" }}>
                                            #{row.id}
                                        </td>
                                        <td style={{ padding: "9px 10px", fontSize: "12px", color: "var(--savvy-text)", fontWeight: 500 }}>
                                            {STRATEGY_LABELS[row.strategy] || row.strategy}
                                        </td>
                                        <td style={{ padding: "9px 10px" }}>
                                            <GovernanceBadge state={row.governance_state} />
                                        </td>
                                        <td style={{ padding: "9px 10px", minWidth: 220 }}>
                                            <OutcomeConfidencePanel
                                                outcomeClassification={row.outcome_classification}
                                                evidenceConfidence={row.evidence_confidence}
                                                compact
                                                showUnavailable
                                            />
                                        </td>
                                        <td style={{ padding: "9px 10px", fontSize: "12px", fontWeight: 600, fontVariantNumeric: "tabular-nums",
                                            color: row.overfit_score > 0.3 ? "#f87171" : row.overfit_score > 0.2 ? "#fbbf24" : "#4ade80"
                                        }}>
                                            {row.overfit_score != null ? row.overfit_score.toFixed(3) : "—"}
                                        </td>
                                        <td style={{ padding: "9px 10px", fontSize: "12px", fontVariantNumeric: "tabular-nums",
                                            color: row.avg_ruin_probability > 0.2 ? "#f87171" : row.avg_ruin_probability > 0.1 ? "#fbbf24" : "var(--savvy-text)"
                                        }}>
                                            {pct(row.avg_ruin_probability)}
                                        </td>
                                        <td style={{ padding: "9px 10px", fontSize: "11px", color: "var(--savvy-muted)", whiteSpace: "nowrap" }}>
                                            {fmtDate(row.validation_timestamp)}
                                        </td>
                                        <td style={{ padding: "9px 10px", fontSize: "11px", color: "var(--savvy-dim)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {row.notes || "—"}
                                        </td>
                                        <td style={{ padding: "9px 10px" }}>
                                            {onSelectRun && (
                                                <button
                                                    onClick={() => onSelectRun(row.id)}
                                                    style={{
                                                        background: "none",
                                                        border: "1px solid rgba(255,255,255,0.12)",
                                                        color: "var(--savvy-muted)",
                                                        borderRadius: 4,
                                                        padding: "2px 8px",
                                                        fontSize: "11px",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    View
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr>
                                        <td colSpan={9} style={{ padding: "24px 10px", textAlign: "center", color: "var(--savvy-dim)", fontSize: "12px" }}>
                                            No validations found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
                            <button
                                onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                disabled={filters.page <= 1}
                                style={{
                                    padding: "4px 12px",
                                    background: "var(--savvy-panel)",
                                    border: "1px solid var(--savvy-border)",
                                    borderRadius: 6,
                                    color: "var(--savvy-muted)",
                                    fontSize: "12px",
                                    cursor: filters.page <= 1 ? "not-allowed" : "pointer",
                                    opacity: filters.page <= 1 ? 0.4 : 1,
                                }}
                            >
                                ← Prev
                            </button>
                            <span style={{ fontSize: "12px", color: "var(--savvy-muted)", padding: "4px 8px" }}>
                                {filters.page} / {totalPages}
                            </span>
                            <button
                                onClick={() => setFilters(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                                disabled={filters.page >= totalPages}
                                style={{
                                    padding: "4px 12px",
                                    background: "var(--savvy-panel)",
                                    border: "1px solid var(--savvy-border)",
                                    borderRadius: 6,
                                    color: "var(--savvy-muted)",
                                    fontSize: "12px",
                                    cursor: filters.page >= totalPages ? "not-allowed" : "pointer",
                                    opacity: filters.page >= totalPages ? 0.4 : 1,
                                }}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
